import React from 'react';
import { PageHero } from '../components/PageHero';
import { SectionStage } from '../components/SectionStage';
import { Link } from 'react-router-dom';
/**
 * Terms of Service — early-access pre-GA terms. Covers website + program
 * participation. Active validation engagements are governed by a separate
 * Master Subscription Agreement when customers sign on.
 */
const LAST_UPDATED = '2026-01-15';
const sections: {
  title: string;
  body: React.ReactNode;
}[] = [{
  title: '1. Acceptance',
  body: <p data-id="element-1757">
        These Terms of Service ("Terms") form a binding agreement between you
        and <strong data-id="element-1758">Periscan, Inc.</strong> ("Periscan", "we", "us"). By
        accessing the periscan.com website, submitting an early-access request,
        or otherwise using anything we call "the Services" below, you agree to
        these Terms. If you do not agree, do not use the Services.
      </p>
}, {
  title: '2. What "the Services" means',
  body: <>
        <p data-id="element-1759">For purposes of these Terms, "Services" means:</p>
        <ul data-id="element-1760">
          <li data-id="element-1761">The periscan.com website and any subdomains.</li>
          <li data-id="element-1762">
            The early-access program — including forms, qualification
            communications, briefings, demos, and any pre-GA preview materials
            we share.
          </li>
          <li data-id="element-1763">
            Any documentation, marketing content, or machine-readable briefs we
            publish (for example, /llms and /llms.txt).
          </li>
        </ul>
        <p data-id="element-1764">
          Active validation engagements — where Periscan accesses a customer's
          environment to run validation missions — are <strong data-id="element-1765">not</strong>{' '}
          covered by these Terms. Those engagements are governed by a separate
          Master Subscription Agreement and Data Processing Addendum executed
          with the customer.
        </p>
      </>
}, {
  title: '3. Who can use the Services',
  body: <p data-id="element-1766">
        You must be at least 16 years old and legally able to enter contracts in
        your jurisdiction. If you are using the Services on behalf of a company
        or other entity, you represent that you have authority to bind that
        entity, and "you" includes both you and that entity.
      </p>
}, {
  title: '4. Early access',
  body: <>
        <p data-id="element-1767">
          The Services are pre-general-availability. We may approve, defer, or
          decline early-access requests for any reason, including program fit,
          capacity, and prioritization of teams with active validation needs.
        </p>
        <p data-id="element-1768">
          Early-access participation does not guarantee a future commercial
          contract, pricing, or feature set. Pricing, scope, and packaging will
          be finalized as the program matures.
        </p>
      </>
}, {
  title: '5. Acceptable use',
  body: <>
        <p data-id="element-1769">You agree not to:</p>
        <ul data-id="element-1770">
          <li data-id="element-1771">
            Use the Services to violate any law, regulation, or third-party
            right.
          </li>
          <li data-id="element-1772">
            Attempt to disrupt, degrade, or gain unauthorized access to the
            Services, our infrastructure, or other users' data — without a
            written, scoped, authorized engagement that says otherwise.
          </li>
          <li data-id="element-1773">
            Reverse engineer, decompile, scrape, or copy non-public portions of
            the Services for the purpose of building a competing product.
          </li>
          <li data-id="element-1774">
            Use the Services to develop, train, or evaluate AI models in a way
            that misrepresents Periscan or our positioning.
          </li>
          <li data-id="element-1775">
            Submit false, misleading, or impersonated information through our
            forms.
          </li>
        </ul>
        <p data-id="element-1776">
          We may suspend or terminate access for any user who violates these
          rules.
        </p>
      </>
}, {
  title: '6. Intellectual property',
  body: <>
        <p data-id="element-1777">
          Periscan and its licensors own all rights, title, and interest in and
          to the Services — including the website, brand, logos, trademarks,
          copy, software, documentation, and underlying technology. We grant you
          a limited, non-exclusive, non-transferable license to access and use
          the Services for their intended purpose. All rights not expressly
          granted are reserved.
        </p>
        <p data-id="element-1778">
          Content you submit through our forms remains yours, but you grant
          Periscan a worldwide, royalty-free license to use that content for the
          purposes described in our{' '}
          <Link to="/privacy" className="text-[#1a8fd4] hover:underline" data-id="element-1779">
            Privacy Policy
          </Link>
          .
        </p>
      </>
}, {
  title: '7. Feedback',
  body: <p data-id="element-1780">
        If you provide feedback, suggestions, or ideas about the Services
        ("Feedback"), you grant Periscan a perpetual, irrevocable, royalty-free,
        worldwide license to use, modify, and incorporate the Feedback into the
        Services without obligation or compensation. Feedback that is genuinely
        useful is the whole point of early access — thank you in advance.
      </p>
}, {
  title: '8. Confidentiality',
  body: <p data-id="element-1781">
        During early access, we may share non-public roadmap, design, technical,
        or pricing information ("Confidential Information"). You agree to treat
        that information with the same care you use for your own confidential
        information (and at minimum, reasonable care), to use it only to
        evaluate the Services, and not to disclose it to third parties without
        our written consent. This obligation survives for two years after
        disclosure.
      </p>
}, {
  title: '9. Third-party content and links',
  body: <p data-id="element-1782">
        The Services may link to or reference third-party websites, tools, or
        content. Periscan does not control and is not responsible for
        third-party content. Following a link to a third party is at your own
        risk.
      </p>
}, {
  title: '10. Disclaimers',
  body: <p className="uppercase tracking-wide text-sm" data-id="element-1783">
        The Services are provided "as is" and "as available", without warranties
        of any kind, express or implied — including merchantability, fitness for
        a particular purpose, non-infringement, and any warranties arising from
        course of dealing or usage of trade. Periscan does not warrant that the
        Services will be uninterrupted, error-free, secure, or that any specific
        result will be achieved through early-access participation.
      </p>
}, {
  title: '11. Limitation of liability',
  body: <p className="uppercase tracking-wide text-sm" data-id="element-1784">
        To the maximum extent permitted by law, Periscan and its officers,
        directors, employees, and agents will not be liable for any indirect,
        incidental, special, consequential, exemplary, or punitive damages —
        including loss of profits, revenue, data, or business opportunities —
        arising out of or related to your use of the Services, even if advised
        of the possibility of such damages. Periscan's total aggregate liability
        under these Terms will not exceed one hundred U.S. dollars (US$100).
      </p>
}, {
  title: '12. Indemnification',
  body: <p data-id="element-1785">
        You agree to defend, indemnify, and hold harmless Periscan from any
        third-party claims, damages, liabilities, and expenses (including
        reasonable attorneys' fees) arising out of (a) your use of the Services
        in violation of these Terms or applicable law, or (b) your violation of
        any third-party right.
      </p>
}, {
  title: '13. Termination',
  body: <p data-id="element-1786">
        We may suspend or terminate your access to the Services at any time,
        with or without notice, for any reason — including suspected violation
        of these Terms. You may stop using the Services at any time. Sections
        that by their nature should survive termination (including intellectual
        property, confidentiality, disclaimers, limitation of liability,
        indemnification, and governing law) will survive.
      </p>
}, {
  title: '14. Governing law',
  body: <p data-id="element-1787">
        These Terms are governed by the laws of the State of Delaware, United
        States, without regard to its conflict-of-laws principles. The exclusive
        venue for any dispute arising out of these Terms or the Services is the
        state and federal courts located in Delaware, and the parties consent to
        personal jurisdiction in those courts.
      </p>
}, {
  title: '15. Changes to these Terms',
  body: <p data-id="element-1788">
        We may update these Terms from time to time. When we do, we will revise
        the "Last updated" date above and, for material changes, provide
        additional notice. Your continued use of the Services after the update
        means you accept the revised Terms.
      </p>
}, {
  title: '16. Contact',
  body: <p data-id="element-1789">
        Questions about these Terms? Email{' '}
        <a href="mailto:legal@periscan.com" data-id="element-1790">legal@periscan.com</a>. For privacy
        requests specifically, use{' '}
        <a href="mailto:privacy@periscan.com" data-id="element-1791">privacy@periscan.com</a>.
      </p>
}];
export function Terms() {
  return <>
      <PageHero eyebrow="Terms" title="Terms of Service." titleMuted="The rules of the road." description="Early-access terms for the Periscan website and program. Active validation engagements are governed by a separate Master Subscription Agreement." bg="none" data-id="element-1792" />

      <SectionStage fx="none" data-id="element-1793">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1794">
          <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-1795">
            <div className="flex items-baseline justify-between gap-4 mb-12 pb-6 border-b border-white/5" data-id="element-1796">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40" data-id="element-1797">
                Last updated · {LAST_UPDATED}
              </p>
              <Link to="/privacy" className="text-xs text-white/50 hover:text-[#1a8fd4] transition-colors underline underline-offset-4 decoration-white/20" data-id="element-1798">
                Read the Privacy Policy →
              </Link>
            </div>

            <div className="prose prose-invert max-w-none space-y-12 text-[15px] leading-relaxed text-white/75 [&_h2]:text-white [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:mb-4 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ul]:mb-4 [&_a]:text-[#1a8fd4] [&_a:hover]:underline [&_strong]:text-white" data-id="element-1799">
              {sections.map(s => <article key={s.title} data-id="element-1800">
                  <h2 data-id="element-1801">{s.title}</h2>
                  {s.body}
                </article>)}

              <p className="pt-12 mt-12 border-t border-white/5 text-xs text-white/40 italic" data-id="element-1802">
                These Terms are provided for the early-access period and do not
                constitute legal advice. Periscan reserves the right to replace
                these Terms with counsel-reviewed versions before general
                availability.
              </p>
            </div>
          </div>
        </section>
      </SectionStage>
    </>;
}